"use client";

import { Check } from "lucide-react";
import { SiWechat } from "@icons-pack/react-simple-icons";
import { Button } from "@/components/ui/button";
import { useCopyToClipboard } from "@/hooks/useCopyToClipboard";

/**
 * Client leaf for the /collabs "WeChat" contact chip. Isolated so the rest of
 * the collabs page renders as a SERVER component — the page is otherwise fully
 * static, and only this copy-to-clipboard needs hydration (previously the whole
 * page shipped as one big client component just for this one interaction).
 *
 * `legacyFallback` is REQUIRED here and is not decoration: this button shipped
 * its own off-screen-textarea + `execCommand` path because WeChat users
 * frequently arrive from an in-app browser where the async Clipboard API is
 * absent or blocked. Dropping to the hook's default (no fallback) would have
 * silently removed that.
 *
 * ⚠ One deliberate behaviour CORRECTION came with the migration: the hand-rolled
 * version called `toast.success` UNCONDITIONALLY — including when the async API
 * and `execCommand` had both failed — so it told people their clipboard held an
 * ID it did not. It now reports the failure and names the ID so they can read it
 * off the screen.
 */
export function WeChatCopyButton({
  wechatId,
  iconColor,
}: {
  wechatId: string;
  iconColor: string;
}) {
  const { copied, copy } = useCopyToClipboard();

  async function handleCopy() {
    await copy(wechatId, {
      success: "WeChat ID copied to clipboard!",
      error: `Couldn't copy — the WeChat ID is ${wechatId}`,
      confirmForMs: 2000,
      legacyFallback: true,
    });
  }

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className="min-h-11 sm:h-9 sm:min-h-0 border-border/30"
      onClick={handleCopy}
    >
      {copied ? (
        <>
          <Check className="h-3.5 w-3.5" />
          Copied
        </>
      ) : (
        <>
          <SiWechat size={14} color={iconColor} />
          WeChat
        </>
      )}
    </Button>
  );
}
